import type { ProductionLanguageCode } from "./brandProduction";

export const folders = ["known_winner", "expected_winner", "unknown"] as const;
export type Folder = (typeof folders)[number];

export function sourcePlatformLabel(value: string | null | undefined): string {
  if (!value || value === "proprietary" || value === "unknown_needs_review") return "Unknown";
  return value.replaceAll("_", " ");
}

export const ingestionStates = ["uploaded", "queued", "processing", "recovering", "review_required", "ready", "failed"] as const;
export type IngestionState = (typeof ingestionStates)[number];
export const workflowScopes = ["primary_ingestion", "reference_maintenance", "non_ingestion"] as const;
export type WorkflowScope = (typeof workflowScopes)[number];
export const creativeMedia = ["video", "image"] as const;
export type CreativeMedium = (typeof creativeMedia)[number];
export type ReferenceWorkflowProfile = "reference_video" | "static_ad";
export type AdIntentWorkflowProfile = ReferenceWorkflowProfile | "static_ad_animation";

export type ProductTag = {
  slug: string;
  label: string;
  dimension: "product_class" | "form" | "vertical" | "audience" | "condition" | "outcome" | "mechanism" | "ingredient";
};

export type CreativeFormatTag = {
  slug: string;
  label: string;
  dimension: "creative_format";
};

export type AdTag = ProductTag | CreativeFormatTag;

export type TagFacet = AdTag & { count: number };

export type AdSummary = {
  id: string;
  publicId: string | null;
  brandPublicId: string | null;
  productPublicId: string | null;
  filename: string;
  brand: string;
  product: string;
  category: string;
  tags: AdTag[];
  productTags?: ProductTag[];
  creativeTags?: CreativeFormatTag[];
  scriptTitle: string;
  folder: Folder;
  ingestionState: IngestionState;
  maintenanceIssueCount: number;
  readiness: string;
  sourceUrl: string | null;
  destinationUrl: string | null;
  notes: string;
  sourcePlatform: string;
  creativeMedium: CreativeMedium;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  durationSeconds: number | null;
  byteSize: number | null;
  mediaSha256: string | null;
  localReferenceId: string | null;
  strategyDepartmentId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  mediaUrl: string | null;
  posterUrl: string | null;
};

export type AdSection = {
  section: string;
  title: string;
  content: unknown;
  sourcePath: string | null;
  sourceSha256: string | null;
};

export type Artifact = {
  id: string;
  kind: string;
  label: string;
  sourcePath: string | null;
  mimeType: string;
  byteSize: number | null;
  sha256: string | null;
  url: string;
};

export type ReviewTask = {
  id: string;
  adId: string;
  ingestionId: string | null;
  workflowScope: WorkflowScope;
  kind: "duplicate" | "cut_boundary" | "provider_execution" | "script_authority";
  status: "open" | "resolved" | "dismissed";
  payload: unknown;
  decision: unknown;
  createdAt: string;
  resolvedAt: string | null;
  ad?: AdSummary;
  existingAd?: AdSummary | null;
};

export type DuplicateReviewDecision = { action: "use_existing" | "keep_both" };

export type CutContinuityRelationship = "same_setup_continuation" | "new_setup";

export type CutBoundaryDecision = {
  action: "resolve_boundaries";
  boundaries: Array<
    | { boundaryFrame: number; status: "confirmed_cut"; continuityRelationship: CutContinuityRelationship }
    | { boundaryFrame: number; status: "rejected_candidate" }
    | { boundaryFrame: number; status: "corrected_cut"; correctedBoundaryFrame: number; continuityRelationship: CutContinuityRelationship }
  >;
};

export type NormalizedRegion = { x: number; y: number; width: number; height: number };

export type CharacterTrackInterval = {
  intervalId: string;
  cutId: string;
  startFrame: number;
  endFrame: number;
  startSeconds: number;
  endSeconds: number;
  visualPresence: "full_frame" | "split_top" | "split_bottom" | "split_left" | "split_right" | "corner_inset" | "custom_region";
  normalizedRegion: NormalizedRegion;
  performanceMode: "presenter_lip_sync" | "character_non_speaking";
  detectorEvidence: Record<string, unknown>;
  qwenCutObservationIds?: string[];
};

export type CharacterTrackProjection = {
  trackId: string;
  label: string;
  intervals: CharacterTrackInterval[];
  identityEvidenceCrops: Array<Record<string, unknown>>;
};

export type CutCharacterOperation =
  | { op: "create_track"; trackId: string }
  | { op: "rename_track"; trackId: string; label: string }
  | { op: "merge_tracks"; trackIds: string[]; mergedTrackId: string }
  | { op: "remove_track"; trackId: string }
  | { op: "assign_appearance"; trackId: string; cutId: string }
  | { op: "remove_appearance"; trackId: string; cutId: string }
  | { op: "layout_correction"; trackId: string; cutId: string; normalizedRegion: NormalizedRegion }
  | { op: "performance_mode_correction"; trackId: string; cutId: string; performanceMode: CharacterTrackInterval["performanceMode"] };

export type HostedCutReviewV5Decision = {
  action: "resolve_persistent_character_map";
  schemaVersion: "hosted-cut-review-decision-v5";
  baseCandidateSha256: string;
  cutManifestSha256: string;
  continuitySha256: string;
  idempotencyKey: string;
  cutDecisions: CutBoundaryDecision["boundaries"];
  cutCharacterDecisions: Array<{
    cutId: string;
    characterTrackIds: string[];
  }>;
  finalTracks: CharacterTrackProjection[];
  resolvedIssueIds: string[];
  operations: CutCharacterOperation[];
};

export type HostedCutReviewDraftV1 = {
  schemaVersion: "hosted-cut-review-draft-v1";
  reviewId: string;
  baseCandidateSha256: string;
  cutManifestSha256: string;
  continuitySha256: string;
  cutCharacterDecisions: HostedCutReviewV5Decision["cutCharacterDecisions"];
  finalTracks: CharacterTrackProjection[];
  cutDecisions: Record<string, unknown>;
  operations: CutCharacterOperation[];
  updatedAt?: string;
};

export type ProviderExecutionDecision = {
  action: "approve_one_call" | "reject";
  requestId: string;
  requestSha256: string;
};

export type ReviewDecision = DuplicateReviewDecision | CutBoundaryDecision | HostedCutReviewV5Decision | ProviderExecutionDecision;

export type ReviewDecisionLineage = {
  schemaVersion: "hosted-review-decision-v1";
  decisionId: string;
  reviewId: string;
  adId: string;
  ingestionId: string | null;
  kind: ReviewTask["kind"];
  decision: ReviewDecision;
  decidedBy: "authenticated_operator";
  decidedAt: string;
};

export type RunnerHealth = {
  state: "online" | "busy" | "offline" | "never_connected";
  lastSeenAt: string | null;
  hostname: string | null;
  activeJobId: string | null;
  runtimeHealthy: boolean;
  capabilities: string[];
  guidance: string[];
};

export type LibraryResponse = {
  items: AdSummary[];
  total: number;
  counts: Record<Folder | "all", number>;
  availableTags: TagFacet[];
  openReviewCount: number;
  runner: RunnerHealth;
};

export type BrandSummary = {
  slug: string;
  publicId: string | null;
  name: string;
  adCount: number;
  homepageUrl: string | null;
  heroPath: string;
  isClient: boolean;
  productionLanguage: ProductionLanguageCode;
};

export type BrandDetailResponse = {
  brand: BrandSummary;
  ads: AdSummary[];
  brandKit: BrandKit;
  brandWideAssets: BrandContentAsset[];
  /** Compatibility alias retained for one release. */
  content: BrandContentAsset[];
  productKits: ProductKit[];
  voices: VoiceAsset[];
};

export const productKitAssetRoles = ["primary_product", "packaging", "consumable_or_component", "detail_or_material", "product_in_use", "bundle_or_variant", "contextual_evidence"] as const;
export type ProductKitAssetRole = (typeof productKitAssetRoles)[number];
export type ProductKitApprovalStatus = "pending_review" | "approved_identity" | "approved_direct_use" | "contextual_only" | "rejected";
export type GeneratedProductSourceWorkflow = "reference_video";

export type ProductKitAsset = {
  id: string;
  filename: string;
  title: string;
  assetRole: ProductKitAssetRole;
  mimeType: string;
  mediaUrl: string;
  previewUrl: string | null;
  byteSize: number;
  approvalStatus: ProductKitApprovalStatus;
  analysisStatus: "queued" | "processing" | "ready" | "failed";
  visualAnalysisSummary: string;
  visualAnalysisPath: string | null;
  visibleFeatures: string[];
  operatorNotes: string;
  packetPath: string | null;
  originKind: "user_supplied" | "approved_generated";
  sourceSha256: string | null;
  sourceRunId: string | null;
  sourceUnitId: string | null;
  generationProvider: string | null;
  lineagePath: string | null;
  lineageSha256: string | null;
  sourceWorkflowProfile: GeneratedProductSourceWorkflow | null;
  scratchStyle: null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProductKit = {
  id: string;
  publicId: string | null;
  brandSlug: string;
  name: string;
  sku: string;
  notes: string;
  facts: ProductFacts;
  factsProvenance: Record<string, FieldProvenance>;
  commerceRevision: ProductKitCommerceRevision | null;
  assets: ProductKitAsset[];
  footage: BrandContentAsset[];
  createdAt: string;
  updatedAt: string;
};

export const productKitCommerceFactTypes = [
  "current_price", "compare_at_price", "discount", "promotion", "purchase_cadence",
  "payment_method", "shipping", "included_gift", "guarantee", "cta", "quantity",
  "timeframe", "proof_value",
] as const;
export type ProductKitCommerceFactType = (typeof productKitCommerceFactTypes)[number];
export type ProductKitCommerceFact = {
  id: string;
  type: ProductKitCommerceFactType;
  normalizedValue: string;
  displayValue: string;
  state: "verified" | "unknown" | "expired";
  provenance: FieldProvenance & { evidence?: string };
  effectiveFrom: string | null;
  effectiveTo: string | null;
};
export type ProductKitCommerceRevision = {
  id: string;
  revisionNumber: number;
  revisionSha256: string;
  facts: ProductKitCommerceFact[];
  createdAt: string;
};

export type CreativeStrategyAngleSummary = {
  angleId: string;
  territoryId: string;
  angleProposition: string;
  awareness: string;
  personaId?: string;
  persuasionMechanic?: "gain_realization" | "consequence_of_inaction" | "costly_alternative";
};

export type CreativeStrategyStatus = {
  productKitId: string;
  productPublicId: string;
  currentSourceFingerprint: string;
  readiness: "missing" | "strategy_incomplete" | "awaiting_approval" | "approved" | "stale" | "failed";
  /** Latest reviewable revision, regardless of approval state. */
  revision: null | {
    id: string;
    schemaVersion: "creative-strategy-revision-v7" | "creative-strategy-revision-v8";
    status: string;
    sourceFingerprint: string;
    artifactSha256: string | null;
    localPacketPath: string | null;
    summary: {
      evidenceCount?: number;
      qualityStatus?: "awaiting_approval" | "strategy_incomplete" | "approved";
      runOutcome?: "complete" | "degraded";
      reportLanguage?: "en";
      advisoryIssueCount?: number;
      blockingIssueCount?: number;
      retainedAngleCount?: number;
      compiledContextPackCount?: number;
      reviewArtifactBytes?: number;
      providerEnvelopeCount?: number;
      personaCount?: number;
      socialProofSignalCount?: number;
      competitorCount?: number;
      candidateCount?: number;
      initialSlotTarget?: number;
      generatedSlotCount?: number;
      persuasionMechanicCounts?: Record<string, number>;
      territoryCount?: number;
      verifiedAngleCount?: number;
      activeAngles?: CreativeStrategyAngleSummary[];
    };
    staleReason: string | null;
    approvedAt: string | null;
    createdAt: string;
  };
  /** Latest v8 revision awaiting approval, when present. */
  pendingRevision: CreativeStrategyStatus["revision"];
  pendingRevisionError?: { revisionId: string; code: string; message: string } | null;
  /** Latest approved revision from the current source fingerprint. */
  approvedRevision: CreativeStrategyStatus["revision"];
  /** Approved revision used by production selectors while a replacement is pending. */
  retainedRevision: CreativeStrategyStatus["revision"];
  refreshJob: null | {
    id: string;
    status: string;
    currentStage: string;
    progress: number;
    reasons: string[];
    error: string | null;
    stageStartedAt: string | null;
    lastHeartbeatAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
};

export type StrategyReviewListItem = {
  id: string;
  schemaVersion: "creative-strategy-revision-v7" | "creative-strategy-revision-v8";
  status: "awaiting_approval" | "strategy_incomplete" | "approved";
  productKitId: string;
  productPublicId: string;
  productName: string;
  brandSlug: string;
  brandPublicId: string;
  brandName: string;
  artifactSha256: string;
  reviewObjectSha256: string;
  summary: CreativeStrategyStatus["revision"] extends { summary: infer T } ? T : Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type StrategyReviewDetail = {
  revision: Omit<StrategyReviewListItem, "summary" | "reviewObjectSha256">;
  artifact: Record<string, unknown>;
  history: Array<Record<string, unknown> & { focus: string[] }>;
  approval?: null | { status: "queued" | "leased" | "failed" | "completed"; error: string | null };
};

export type ScratchCreativeIntent = {
  id: string;
  workflowProfile: "scratch_3d";
  authoritySha256: string;
  createsProductionWork: false;
  status: "ready";
  authority: Record<string, unknown>;
  launchBudget: {
    schemaVersion: "scratch-launch-budget-v1";
    policySha256: string;
    maximumPreReviewSpendUsd: number;
    plannedSpendUsd: number;
    recoveryReserveUsd: number;
    hardMaximumUsd: number | null;
    breakdown: {
      planningModelSpendCeilingUsd: number;
      maximumCharacterSheets: number;
      characterSheetSpendCeilingUsd: number;
      downstreamPlannedAllowanceUsd: number;
    };
    internalExpectedCalls: Record<string, number>;
  };
};

export type ScratchCreativeRun = {
  id: string;
  publicId: string;
  workflowProfile: "scratch_3d";
  status: "initializing" | "running" | "waiting_for_review" | "blocked" | "complete";
  currentGate: null | "scratch_creative_plan" | "scratch_staging_draft" | "scratch_final_acceptance";
  localRunPath: string | null;
  memberPublicIds: string[];
  reviewGates: ["scratch_creative_plan", "scratch_staging_draft", "scratch_final_acceptance"] | ["scratch_creative_plan", "scratch_final_acceptance"];
};

export type StyleStatus = "active" | "needs_review" | "archived";

export type StyleSource = {
  id: string;
  kind: "reference_ad" | "upload";
  ad: AdSummary | null;
  mediaUrl: string | null;
  previewUrl: string | null;
  mediaType: string | null;
  mediaSha256: string | null;
  originalFilename: string | null;
  position: number;
  archivedAt: string | null;
  createdAt: string;
};

export type Style = {
  id: string;
  publicId: string;
  name: string;
  description: string;
  status: StyleStatus;
  visualTraits: string[];
  provenance: Record<string, unknown>;
  recipeId: string | null;
  formatProfileSignature: string | null;
  scratch3d?: null | {
    profileSha256: string;
    calibrationStatus: "pending_examples" | "pending_provider_fixture" | "passed" | "failed";
    capabilities: { narration: boolean; directSpeech: boolean; dialogue: boolean; mechanismAnimation: boolean; productIntegration: boolean };
  };
  sources: StyleSource[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StyleListResponse = { items: Style[]; total: number };

export type AdIntentIssueCode =
  | "missing_id" | "duplicate_id" | "unknown_id" | "archived_entity" | "ambiguous_alias"
  | "style_not_active" | "product_brand_mismatch" | "brand_kit_not_ready"
  | "product_kit_not_ready" | "reference_not_ready" | "reference_medium_mismatch" | "generated_ad_not_reference";

export type AdIntentIssue = { code: AdIntentIssueCode; field: "brand" | "style" | "product" | "referenceAd"; message: string };

export type AdIntentResolution = {
  contractVersion: "ad-intent-resolution-v1" | "ad-intent-resolution-v2" | "ad-intent-resolution-v3" | "ad-intent-resolution-v4";
  workflowProfile: AdIntentWorkflowProfile;
  creativeMedium: CreativeMedium;
  valid: boolean;
  issues: AdIntentIssue[];
  entities: {
    brand: BrandSummary | null;
    style: Style | null;
    product: ProductKit | null;
    referenceAd: AdSummary | null;
  };
  receipt: null | { id: string; sha256: string; createdAt: string };
};

export type VideoVariationAxisKind = "visual_hook" | "spoken_hook" | "script" | "voice" | "style";
export type StaticVariationAxisKind = "visual_hook" | "headline" | "supporting_copy" | "product_presentation" | "visual_treatment";
export type VariationAxisKind = VideoVariationAxisKind | StaticVariationAxisKind;
export type VariationAxisValue = { id: string; label: string; instruction: string; resourceId: string | null; voiceId: string | null; ttsSpeed: number | null; isBaseline: boolean };
export type VariationAxis = { kind: VariationAxisKind; label: string; enabled: boolean; values: VariationAxisValue[] };
export type VariationMatrixMember = { position: number; label: string; axisValues: Partial<Record<VariationAxisKind, VariationAxisValue>>; signature: string; generatedAdPublicId?: string };
export type VariationMatrixPreview = {
  contractVersion: "variation-matrix-preview-v1" | "variation-matrix-preview-v2";
  workflowProfile: ReferenceWorkflowProfile;
  creativeMedium: CreativeMedium;
  candidatesPerMember: 1 | 2;
  providerImageCount: number;
  intentReceiptId: string;
  intentReceiptSha256: string;
  strategyRevisionId: string;
  strategyCandidateSha256: string;
  selectedAngleId: string;
  contextPackSha256: string;
  matrixSha256: string;
  calculation: string;
  total: number;
  axes: VariationAxis[];
  members: VariationMatrixMember[];
  warnings: string[];
  valid: boolean;
};

export type VariationSet = {
  id: string;
  publicId: string;
  runId: string;
  contractVersion: "variation-set-v1" | "variation-set-v2";
  workflowProfile: ReferenceWorkflowProfile;
  creativeMedium: CreativeMedium;
  candidatesPerMember: 1 | 2;
  brandSlug: string;
  brandPublicId: string;
  productKitId: string;
  productPublicId: string;
  productName: string;
  referenceAdId: string;
  referencePublicId: string;
  strategyRevisionId: string | null;
  strategyCandidateSha256: string | null;
  selectedAngleId: string | null;
  contextPackSha256: string | null;
  matrixSha256: string;
  total: number;
  status: "initializing" | "active" | "blocked" | "complete";
  progress: {
    initialization: "queued" | "leased" | "completed" | "failed";
    planning: "not_started" | "queued" | "leased" | "completed" | "failed";
    currentGate: ProductionGateKind | null;
    updatedAt: string;
    error: string | null;
  };
  createdAt: string;
  members: VariationMatrixMember[];
};

export type ProductionGateKind = "consolidated_creative_plan" | "image_batch_review" | "complete_draft_review" | "video_batch_review" | "final_acceptance" | "static_creative_plan" | "static_candidate_batch_review" | "static_final_batch_acceptance" | "scratch_creative_plan" | "scratch_staging_draft" | "scratch_final_acceptance";
export type ScratchCorrectionKind = "script_patch" | "storyboard_patch" | "character_profile_patch" | "character_sheet_regeneration" | "character_reclassification" | "style_patch" | "staging_image_regeneration" | "video_regeneration" | "literal_image_replacement" | "literal_video_replacement" | "voice_selection" | "audio_replacement" | "caption_only" | "assembly_only" | "model_override";
export type ScratchCorrectionEdit = {
  edit_id: string;
  kind: ScratchCorrectionKind;
  target: { component_id: string; cut_id?: string | null; asset_sha256?: string | null; timestamp_seconds?: number | null };
  instruction: string;
  output_mode: "patch" | "regenerate" | "replace" | "recompile";
  model_override?: { requested_model: string; fallback_policy: "approved_router_secondary" | "model_only" } | null;
  resource_override_id?: string | null;
  uploaded_asset?: { path: string; sha256: string; media_type: string } | null;
  provider_authorized: boolean;
};
export type ProductionReviewContentKind = "script" | "image" | "video" | "audio" | "final" | "bundle";
export type ProductionReviewCandidate = {
  id: string;
  revision: number;
  sha256: string;
  parentCandidateId: string | null;
  contentKind: ProductionReviewContentKind;
  content: Record<string, unknown>;
  artifactUrl: string | null;
  items?: Array<{
    position: number;
    stepId: string;
    title: string;
    contentKind: ProductionReviewContentKind;
    content: Record<string, unknown>;
    artifactSha256: string;
    artifactUrl: string | null;
    validationStatus: "valid" | "processing" | "failed";
  }>;
  validationStatus: "valid" | "processing" | "failed";
  createdAt: string;
};
export type ProductionReviewMessage = {
  id: string;
  role: "operator" | "assistant" | "system";
  content: string;
  status: "recorded" | "queued" | "applied" | "failed";
  candidateId: string | null;
  createdAt: string;
};
export type RunnerEligibility = {
  state: "available" | "busy" | "missing";
  jobStatus: "queued" | "leased";
  jobId: string;
  jobKind: string;
  creativeRunId: string | null;
  compatibleRunnerCount: number;
  lastCompatibleSeenAt: string | null;
  guidance: string;
};
/** #73: read-only status of exceptional recovery for a stopped whiteboard job. The
 * action itself stays on the existing Kanban recovery control. */
export type WhiteboardRecoveryStatus = {
  state: 'awaiting_approval' | 'recovering' | 'unavailable';
  stage: string; explanation: string; failedJobId: string | null;
  proposal: { id: string; fixSummary: string; remainingClipIds: string[]; remainingEstimateUsd: number | null; preservedOutputs: number; pollSameTask: number } | null;
};
export type ProductionReviewTask = {
  whiteboardRecovery?: WhiteboardRecoveryStatus | null;
  whiteboardChain?: {
    status: 'running' | 'paused' | 'interrupted' | 'checked_pending_assembly';
    revision: number; checkedClips: number; totalClips: number; updatedAt: string;
    clips: Array<{ clipId: string; videoSha256: string; audioSha256: string; finalFrameSha256: string; durationSeconds: number }>;
    pendingClip: { clipId: string; videoSha256: string; audioSha256: string; findings: Array<{ check: string; verdict: 'defect' | 'uncertain'; evidence: string }> } | null;
    repairs: Array<{ clipId: string; allowance: number; attemptsUsed: number;
      disposition: 'authorized' | 'repaired_checked' | 'defect_cleared_uncertainty_remains' | 'unresolved';
      attempts: Array<{ attempt: number; shotId: string; authorizedBy: string;
        status: 'authorized' | 'repaired_checked' | 'defect_cleared_uncertainty_remains' | 'unresolved';
        defects: Array<{ check: string; verdict: 'defect'; evidence: string }>;
        defectiveVideo: { sha256: string }; repairedVideo: { sha256: string } | null }> }>;
    costs: { settledVideoImageUsd: number; reservedVideoImageUsd: number; observedQaUsd: number | null };
    blocker: { reason: string; regenerationAuthorized: false } | null;
  } | null;
  id: string;
  runId: string;
  variationSetId: string;
  variationSetPublicId: string;
  gate: ProductionGateKind;
  status: "open" | "action_queued" | "correction_processing" | "resolved" | "dismissed";
  brand: { slug: string; publicId: string; name: string };
  product: { id: string; publicId: string; name: string };
  representedAds: number;
  brandGateCount: number;
  focusedStep: number;
  stepCount: number;
  artifactScope: { memberIds: string[]; shared: boolean };
  currentCandidate: ProductionReviewCandidate | null;
  provisionalCorrection?: {
    transactionId: string;
    sourceCandidateSha256: string;
    candidate: ProductionReviewCandidate;
  } | null;
  messages?: ProductionReviewMessage[];
  runnerEligibility?: RunnerEligibility | null;
  nextTaskId?: string | null;
  correctionOptions?: {
    voices: Array<{ id: string; label: string; category: "male" | "female"; previewUrl: string; durationSeconds: number | null }>;
    styles: Array<{ id: string; publicId: string; label: string }>;
  };
  createdAt: string;
  resolvedAt: string | null;
};

export type ProductionKanbanResponse = {
  columns: Array<{ gate: ProductionGateKind; label: string; tasks: ProductionReviewTask[] }>;
  openTaskCount: number;
  recoveryIncidents?: import("./workflowRecovery").KanbanRecoveryIncident[];
  scriptClarifications?: import("./scriptClarification").ScriptClarification[];
};

export type BrandDeliverySla = {
  id: string;
  brandSlug: string;
  timezone: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  activeWeekdays: number[];
  dailyTarget: number;
  createdAt: string;
};
export type DeliveryAsset = {
  id: string;
  brandSlug: string;
  sourceKind: "manual_upload" | "generated_ad";
  sourceAdId: string | null;
  title: string;
  filename: string;
  mimeType: "video/mp4" | "image/jpeg" | "image/png";
  byteSize: number;
  sha256: string;
  mediaUrl: string;
  previewUrl: string | null;
  readiness: "processing" | "ready" | "failed";
  createdAt: string;
};
export type DeliveryAssignment = {
  id: string;
  asset: DeliveryAsset;
  localDate: string;
  status: "scheduled" | "unscheduled" | "delivered" | "failed";
  createdAt: string;
};
export type DeliveryCalendarDay = {
  localDate: string;
  active: boolean;
  target: number;
  readyCount: number;
  assignments: DeliveryAssignment[];
};
export type DeliveryCalendarResponse = {
  brand: BrandSummary;
  timezone: string | null;
  range: { from: string; to: string };
  slas: BrandDeliverySla[];
  days: DeliveryCalendarDay[];
};

export type BrandContentAsset = {
  id: string;
  brandSlug: string;
  product: string;
  kind: "user_supplied" | "approved_stock";
  title: string;
  description: string;
  provider: string | null;
  sourceUrl: string | null;
  durationSeconds: number | null;
  mediaUrl: string;
  posterUrl: string | null;
  ingestionState: IngestionState;
  ingestionError: string | null;
  packetPath: string | null;
  mimeType: string;
  sourceSha256: string | null;
  scopeKind: "brand_wide" | "product_kit";
  productKitId: string | null;
  assignmentStatus: "matched" | "approved_stock_brand_wide" | "unmatched_brand_wide" | "ambiguous_brand_wide" | "explicit_brand_wide" | "explicit_product_kit";
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FieldProvenance = {
  source: "website_capture" | "reference_metadata" | "manual" | "direct_upload" | "migration";
  sourceUrl?: string;
  capturedAt?: string;
  revisionId?: string;
};

export type ProductFacts = {
  url: string;
  description: string;
  price: string;
  offers: string[];
  benefits: string[];
  mechanisms: string[];
  ingredientsOrMaterials: string[];
  proof: string[];
  claims: string[];
  visualAccuracyRules: string[];
  scratchResearchUrls: string[];
};

export type BrandKitIdentityAsset = {
  id: string;
  kind: "logo" | "favicon" | "guidelines";
  title: string;
  mediaUrl: string | null;
  sourceUrl: string | null;
  mimeType: string | null;
  sha256: string | null;
  captureStatus: "ready" | "inaccessible" | "invalid";
};

export type BrandKitFont = {
  id: string;
  family: string;
  style: string;
  weight: string;
  stretch: string;
  variableAxes: Record<string, number | string>;
  roleLabels: string[];
  mediaUrl: string | null;
  sourceStylesheetUrl: string | null;
  sourceFontUrl: string | null;
  mimeType: string | null;
  sha256: string | null;
  captureStatus: "ready" | "exact_file_required" | "invalid";
  effectiveFamily: string;
  fallbackFamily: "Helvetica" | null;
};

export type BrandKitFields = {
  brandName: string;
  homepageUrl: string;
  domain: string;
  category: string;
  tagline: string;
  colors: string[];
  positioning: string;
  differentiators: string[];
  audience: string[];
  voice: string[];
  competitors: string[];
  creativeHistory: string;
  creativeGuardrails: string[];
  deliveryPreferences: string[];
};

export type BrandKitRevision = {
  id: string;
  revisionNumber: number;
  sourceKind: "website_scan" | "manual_edit" | "website_restore";
  fields: BrandKitFields;
  provenance: Record<string, FieldProvenance>;
  revisionSha256: string;
  createdAt: string;
};

export type BrandKit = {
  brandSlug: string;
  homepageUrl: string;
  currentRevision: BrandKitRevision | null;
  assets: BrandKitIdentityAsset[];
  fonts: BrandKitFont[];
  productCandidates: Array<{ id: string; name: string; url: string; description: string; verbatimClaims: string[]; assignmentStatus: "matched" | "needs_product_assignment"; productKitId: string | null }>;
  scanStatus: "idle" | "queued" | "processing" | "ready" | "partial" | "failed";
  scanStage: "queued" | "capturing" | "distilling" | "persisting" | "complete" | "failed" | null;
  lastScanJobId: string | null;
  lastScanError: string | null;
  lastScannedAt: string | null;
  scanRequest: { modelId: "openai/gpt-5.6-luna" };
};

export type VoiceCategory = "male" | "female";

export type VoiceSelection = {
  voiceAssetId: string;
  voiceId: string;
  ttsSpeed: number;
};

export type VoiceAsset = {
  id: string;
  name: string;
  category: VoiceCategory;
  voiceId: string;
  provider: string;
  title: string;
  durationSeconds: number | null;
  mediaUrl: string | null;
  isDisallowedForBrand: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OperatorStatusResponse = {
  openReviewCount: number;
  openProductionReviewCount: number;
  blockedIngestionCount: number;
  needsAttentionCount: number;
  scriptClarificationCount?: number;
  queuedJobCount: number;
  runner: RunnerHealth;
  features: {
    variations: boolean;
    staticAds: boolean;
    staticAdAnimation: boolean;
    scratch3d: boolean;
    scratchSuppliedScript: boolean;
    scratchWhiteboard: boolean;
    kanbanRead: boolean;
    kanbanActions: boolean;
    clientCalendar: boolean;
  };
};

export type OperationItem = {
  jobId: string;
  kind: QueueJob["kind"];
  status: "queued" | "leased" | "failed";
  attempts: number;
  createdAt: string;
  updatedAt: string;
  error: string | null;
  ad: Pick<AdSummary, "id" | "publicId" | "scriptTitle" | "brand" | "product" | "filename" | "strategyDepartmentId" | "ingestionState">;
  ingestion: {
    id: string;
    status: string;
    currentStage: string | null;
    progress: number;
    error: string | null;
  } | null;
  guidance: OperationGuidance;
};

export type OperationGuidance = {
  kind: "retry_ingestion" | "view_ad" | "wait_for_runner" | "processing";
  title: string;
  detail: string;
};

export type OperationReview = {
  id: string;
  kind: ReviewTask["kind"];
  createdAt: string;
  ad: Pick<AdSummary, "id" | "scriptTitle" | "brand" | "product" | "filename">;
};

export type WorkflowBlockerCode =
  | "final_product_kit_promotion_failed"
  | "product_kit_analysis_failed"
  | "brand_content_analysis_failed"
  | "brand_kit_scan_failed"
  | "creative_strategy_refresh_failed"
  | "reference_maintenance_failed"
  | "reference_ingestion_recovery_failed"
  | "workflow_step_failed";

export type WorkflowBlockerGuidance = {
  title: string;
  impact: string;
  recommendedAction: string;
};

export type WorkflowBlocker = {
  id: string;
  fingerprint: string;
  rootCauseKey: string;
  code: WorkflowBlockerCode;
  scopeType: "ad_run" | "reference_ingestion" | "reference_maintenance" | "product_kit" | "brand_content" | "brand_kit" | "runner" | "system";
  scopeId: string;
  contextLabel: string;
  brandSlug: string | null;
  productId: string | null;
  adId: string | null;
  sourceComponent: string;
  sourceStage: string;
  retryMode: "automatic" | "manual" | "none";
  routePath: string | null;
  technicalDetail: string | null;
  evidencePath: string | null;
  status: "open" | "resolved";
  attempts: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
  guidance: WorkflowBlockerGuidance;
};

export type WorkflowIncident = {
  id: string;
  retryMode: WorkflowBlocker["retryMode"];
  affectedCount: number;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  representative: WorkflowBlocker;
  blockers: WorkflowBlocker[];
};

export type CreativeStrategyQueueItem = {
  jobId: string;
  status: "queued" | "leased";
  attempts: number;
  createdAt: string;
  updatedAt: string;
  currentStage: string;
  progress: number;
  queuePosition: number | null;
  productKit: {
    id: string;
    publicId: string | null;
    name: string;
    brandSlug: string;
  };
};

export type OperationsResponse = {
  runner: RunnerHealth;
  counts: { queued: number; active: number; recovering: number; needsAttention: number; openReviews: number; primaryBlocked: number; maintenanceIssues: number };
  items: OperationItem[];
  creativeStrategyJobs: CreativeStrategyQueueItem[];
  incidents: { recovering: WorkflowIncident[]; needsAction: WorkflowIncident[] };
  reviews: OperationReview[];
  history: { failedAttempts: number; items: OperationItem[]; resolvedBlockers: number; blockers: WorkflowBlocker[] };
};

export type AdDetailResponse = {
  ad: AdSummary;
  sections: AdSection[];
  artifacts: Artifact[];
  ingestion: {
    id: string;
    status: string;
    currentStage: string | null;
    progress: number;
    error: string | null;
    stages: Array<{ stage: string; status: string; progress: number; error: string | null }>;
  } | null;
  reviews: ReviewTask[];
};

export type QueueJob = {
  version: 2;
  jobId: string;
  kind: "ingest" | "metadata_sync" | "resume_review" | "resume_pipeline" | "recover_ingestion" | "selected_reference_upgrade" | "cut_review_regeneration" | "cut_continuity_upgrade" | "website_performance_backfill" | "vercel_lifecycle_fixture" | "production_initialize" | "production_feedback" | "production_approve" | "static_plan" | "static_generate" | "static_finalize" | "video_plan" | "delivery_validate" | "scratch_initialize" | "scratch_plan" | "scratch_feedback" | "scratch_approve" | "scratch_creative_package" | "scratch_stage" | "scratch_video" | "scratch_correction";
  adId: string;
  ingestionId: string | null;
  workflowScope: WorkflowScope;
  requiredCapabilities: string[];
  payload: Record<string, unknown>;
  createdAt: string;
};

export type SystemVisualNodeType = "system" | "review" | "gate" | "output" | "route";

export type SystemVisualNode = {
  id: string;
  title: string;
  description: string;
  type: SystemVisualNodeType;
};

export type SystemVisualPhase = {
  id: string;
  title: string;
  summary: string;
  layout: "chain" | "routes";
  steps: SystemVisualNode[];
  routes?: SystemVisualNode[];
  loop_label?: string;
};

export type SystemVisual = {
  schema_version: "system-visual-v1";
  workflow_version: string;
  title: "System Visual";
  subtitle: string;
  verified_at: string;
  source_fingerprint: string;
  source_files: Array<{ path: string; sha256: string }>;
  manifest: { title: string; description: string };
  inputs: Array<{ id: string; title: string; description: string }>;
  phases: SystemVisualPhase[];
  legend: Array<{ type: Exclude<SystemVisualNodeType, "route">; label: string }>;
};
